import clsx from "clsx";
import debounce from "lodash/debounce";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { TransitionStatus } from "react-transition-group";

import * as styles from "@ui/components/navigation/LoopNav/LoopNav.css";
import { LoopNavMenuItem } from "@ui/components/navigation/LoopNav/loopNav.types";
import { sharedStyles } from "@ui/shared-styles";
import { isMobileWidth } from "@voxmedia/duet-utils/device";

import LoopNavSection from "./LoopNavSection";

const MINIMUM_SUBLIST_HEIGHT = 250;

const LoopNavMenu: FunctionComponent<{
  transitionStatus: TransitionStatus;
  sections: ReadonlyArray<LoopNavMenuItem>;
  initialOpenSectionTitle?: string;
  loopNavTitle: string;
}> = ({
  transitionStatus,
  sections,
  initialOpenSectionTitle,
  loopNavTitle,
}) => {
  const [openSection, setOpenSection] = useState<string | undefined>(
    initialOpenSectionTitle,
  );

  /**
   * If vertical space allows, sub-lists within the loopnav should have
   * their own scroll, like inset panels. If there is not enough vertical
   * space for that to happen, we scroll the whole loopnav, which is the
   * default for mobile screens.
   */
  const [isTallEnough, setIsTallEnough] = useState<boolean>(false);

  const listRef = useRef<HTMLOListElement>(null);
  const currentItemRef = useRef<HTMLLIElement>(null);
  const tallEnoughRef = useRef<HTMLElement>(null);

  const checkListHeight = debounce(() => {
    if (!listRef.current) {
      return;
    }

    const listHeight = listRef.current.offsetHeight;
    const contentHeight = Array.from(
      listRef.current.querySelectorAll(`.${styles.sectionTitle}`),
    ).reduce((a, c) => a + (c as HTMLElement).offsetHeight, 0);

    setIsTallEnough(listHeight - contentHeight > MINIMUM_SUBLIST_HEIGHT);
  }, 300);

  const scrollCurrentItemIntoView = () => {
    if (!currentItemRef.current) {
      return;
    }

    const isMobile = isMobileWidth();
    // For wayfinding, we want to show the user where they are right now in the LoopNav.
    // Use "instant"; otherwise it looks like a competing animation.
    currentItemRef.current.scrollIntoView({
      behavior: "instant",
      block: isMobile ? "start" : "center",
    });
  };

  /**
   * Check if the current container is tall enough for us to use the
   * preferred desktop scrolling, where sub-menus are inset panels with
   * their own scroll.
   * Scroll the current item into view afer checking.
   */
  useEffect(() => {
    if (!listRef.current) {
      return;
    }

    const current = listRef.current;

    checkListHeight();
    scrollCurrentItemIntoView();

    const resizeOberver = new ResizeObserver(() => {
      checkListHeight();
    });

    resizeOberver.observe(current);

    return () => {
      resizeOberver.unobserve(current);
    };
  }, [transitionStatus, checkListHeight]);

  /**
   * If the nav container is tall enough to support the preferred desktop
   * scroll pattern, we need to ensure the current item is scrolled back
   * into view for wayfinding. The most reliable way I could find to do this
   * was to check for when the `isTallEnough` class is appended to the
   * loopnav, wait a very short duration, and _then_ scroll the current
   * item into view.
   */
  useEffect(() => {
    if (!tallEnoughRef.current) {
      return;
    }

    const observer = new MutationObserver((muts) => {
      for (const mut of muts) {
        if (mut.attributeName === "class") {
          setTimeout(scrollCurrentItemIntoView, 150);
        }
      }
    });

    observer.observe(tallEnoughRef.current, { attributes: true });

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <nav
      className={clsx(
        styles.menuContainer,
        isTallEnough && styles.tallEnoughContainer,
      )}
      aria-label="Drawer Navigation"
      ref={tallEnoughRef}
    >
      <div className={sharedStyles.accessibility.srOnly}>
        <div id="loopnav-title">Drawer</div>
      </div>
      <ol className={styles.menuList} ref={listRef}>
        {sections.map(({ text: title, items, permalink }) => {
          const open = openSection === title;
          return (
            <LoopNavSection
              key={title}
              text={title}
              loopNavTitle={loopNavTitle}
              items={items}
              onClick={() => {
                setOpenSection(open ? undefined : title);
              }}
              open={open}
              permalink={permalink}
              ref={currentItemRef}
            />
          );
        })}
      </ol>
    </nav>
  );
};

export default LoopNavMenu;
